/**
 * System Logs
 * Admin view of persisted server logs (errors + failed requests) and live health.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  AlertCircle,
  Activity,
  Database,
  Clock,
  Cpu,
  Loader,
  AlertOctagon,
  Bell,
  BellOff,
} from 'lucide-react';
import { systemAPI } from '../api/client';
import { formatIstDateTime } from '../utils/formatIst';

const LEVEL_STYLES = {
  critical: 'bg-red-200 text-red-800',
  error: 'bg-red-100 text-red-700',
  warn: 'bg-amber-100 text-amber-700',
  warning: 'bg-amber-100 text-amber-700',
  info: 'bg-gray-100 text-gray-600',
  access: 'bg-blue-100 text-blue-700',
};

const LEVEL_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'error', label: 'Error' },
  { id: 'warning', label: 'Warning' },
];

const STATUS_STYLE = (status) => {
  if (status >= 500) return 'bg-red-100 text-red-700';
  if (status >= 400) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
};

const SystemLogs = () => {
  const [logType, setLogType] = useState('error');
  const [level, setLevel] = useState('all');
  const [limit, setLimit] = useState(100);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestMsg, setDigestMsg] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await systemAPI.getHealth();
      setHealth(res.data);
    } catch (err) {
      setHealth(err.response?.data || { status: 'error', db: 'error' });
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await systemAPI.getLogs(logType, limit, level);
      setLines(res.data?.lines || []);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load logs';
      setError(msg);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [logType, limit, level]);

  useEffect(() => {
    fetchHealth();
    fetchLogs();
  }, [fetchHealth, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      fetchHealth();
      fetchLogs();
    }, 10000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchHealth, fetchLogs]);

  const showCrashesOnly = () => {
    setLogType('error');
    setLevel('critical');
  };

  const loadDigest = async (send = false) => {
    try {
      setDigestLoading(true);
      setDigestMsg(null);
      const res = await systemAPI.getDigest(send);
      setDigest(res.data?.data || null);
      if (send) {
        setDigestMsg(
          res.data?.sent
            ? 'Digest sent to webhook.'
            : 'Webhook not configured — set ALERT_WEBHOOK_URL on the server.'
        );
      }
    } catch (err) {
      setDigestMsg(err.response?.data?.error || err.message || 'Failed to load digest');
    } finally {
      setDigestLoading(false);
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    try {
      return formatIstDateTime(ts);
    } catch {
      return ts;
    }
  };

  return (
    <div className="admin-page p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Server errors and failed API requests, captured on disk.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={showCrashesOnly}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${
              logType === 'error' && level === 'critical'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
            }`}
          >
            <AlertOctagon className="w-4 h-4" />
            Crashes only
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (10s)
          </label>
          <button
            onClick={() => {
              fetchHealth();
              fetchLogs();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Alert / verbose status strip */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${
            health?.alerts_webhook === 'configured'
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {health?.alerts_webhook === 'configured' ? (
            <Bell className="w-3.5 h-3.5" />
          ) : (
            <BellOff className="w-3.5 h-3.5" />
          )}
          Critical alerts:{' '}
          {health?.alerts_webhook === 'configured' ? 'On (webhook)' : 'Not configured'}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${
            health?.verbose_logging ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          Verbose API logging: {health?.verbose_logging ? 'On' : 'Off'}
        </span>
      </div>

      {/* 24h digest */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-gray-900">24-hour summary</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadDigest(false)}
              disabled={digestLoading}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {digestLoading ? 'Loading…' : 'Load summary'}
            </button>
            <button
              onClick={() => loadDigest(true)}
              disabled={digestLoading || health?.alerts_webhook !== 'configured'}
              title={
                health?.alerts_webhook === 'configured'
                  ? 'Send this summary to the webhook now'
                  : 'Set ALERT_WEBHOOK_URL on the server to enable'
              }
              className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
            >
              Send to webhook
            </button>
          </div>
        </div>
        {digestMsg && <p className="text-xs text-gray-500 mt-2">{digestMsg}</p>}
        {digest && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <DigestStat label="Critical" value={digest.criticalEvents} tone="red" />
            <DigestStat label="Errors" value={digest.errorEvents} tone="red" />
            <DigestStat label="5xx requests" value={digest.server5xx} tone="amber" />
            <DigestStat label="4xx requests" value={digest.client4xx} tone="amber" />
          </div>
        )}
        {digest && digest.topRoutes?.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">Top failing endpoints</div>
            <ul className="text-sm text-gray-700 space-y-1">
              {digest.topRoutes.map(([route, count]) => (
                <li key={route} className="flex justify-between border-b border-gray-50 py-1">
                  <span className="font-mono text-xs break-all">{route}</span>
                  <span className="text-gray-500 ml-2">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Health summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HealthCard
          icon={Activity}
          label="Status"
          value={health?.status || '—'}
          tone={health?.status === 'ok' ? 'green' : 'red'}
        />
        <HealthCard
          icon={Database}
          label="Database"
          value={health?.db || '—'}
          tone={health?.db === 'connected' ? 'green' : 'red'}
        />
        <HealthCard
          icon={Clock}
          label="Uptime"
          value={health?.uptime_s != null ? formatUptime(health.uptime_s) : '—'}
          tone="gray"
        />
        <HealthCard
          icon={Cpu}
          label="Memory"
          value={health?.memory_mb != null ? `${health.memory_mb} MB` : '—'}
          tone="gray"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setLogType('error')}
            className={`px-4 py-2 text-sm ${
              logType === 'error' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Errors
          </button>
          <button
            onClick={() => setLogType('access')}
            className={`px-4 py-2 text-sm ${
              logType === 'access' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Failed Requests
          </button>
        </div>

        {/* Severity filter */}
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          {LEVEL_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setLevel(f.id)}
              className={`px-3 py-2 text-sm ${
                level === f.id ? 'bg-gray-800 text-white' : 'bg-white text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
        >
          <option value={50}>Last 50</option>
          <option value={100}>Last 100</option>
          <option value={250}>Last 250</option>
          <option value={500}>Last 500</option>
        </select>
        {lastUpdated && (
          <span className="text-xs text-gray-400">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Log table */}
      <div className="bg-white rounded-xl border border-gray-200 admin-table-shell">
        {loading && lines.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader className="w-6 h-6 animate-spin" />
          </div>
        ) : lines.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            No {logType === 'access' ? 'failed requests' : 'errors'} logged.
          </div>
        ) : (
          <div className="admin-table-scroll">
            <table className="admin-data-table-xl text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-3 whitespace-nowrap">Time (IST)</th>
                  <th className="px-4 py-3">Level</th>
                  {logType === 'access' ? (
                    <>
                      <th className="px-4 py-3">Method</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">URL</th>
                      <th className="px-4 py-3">ms</th>
                    </>
                  ) : (
                    <th className="px-4 py-3">Message</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((entry, idx) => (
                  <tr key={idx} className="align-top hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {formatTs(entry.ts)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          LEVEL_STYLES[entry.level] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {entry.level || '—'}
                      </span>
                    </td>
                    {logType === 'access' ? (
                      <>
                        <td className="px-4 py-3 font-mono text-xs">
                          {entry.meta?.method || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE(
                              entry.meta?.status
                            )}`}
                          >
                            {entry.meta?.status ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs break-all max-w-md">
                          {entry.meta?.url || entry.message}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {entry.meta?.ms ?? '—'}
                        </td>
                      </>
                    ) : (
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{entry.message || entry.raw}</div>
                        {entry.meta?.error && (
                          <div className="text-red-600 text-xs mt-1">{entry.meta.error}</div>
                        )}
                        {entry.meta?.url && (
                          <div className="text-gray-400 text-xs mt-1 font-mono">
                            {entry.meta.method} {entry.meta.url}
                          </div>
                        )}
                        {entry.meta?.stack && (
                          <pre className="text-gray-400 text-[11px] mt-2 whitespace-pre-wrap break-all max-h-40 overflow-auto bg-gray-50 p-2 rounded">
                            {entry.meta.stack}
                          </pre>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const HealthCard = ({ icon: Icon, label, value, tone }) => {
  const toneMap = {
    green: 'text-green-600 bg-green-50',
    red: 'text-red-600 bg-red-50',
    gray: 'text-gray-600 bg-gray-50',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${toneMap[tone] || toneMap.gray}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="font-semibold text-gray-900 capitalize">{value}</div>
      </div>
    </div>
  );
};

const DigestStat = ({ label, value, tone }) => {
  const toneMap = {
    red: value > 0 ? 'text-red-600' : 'text-gray-400',
    amber: value > 0 ? 'text-amber-600' : 'text-gray-400',
  };
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className={`text-2xl font-bold ${toneMap[tone] || 'text-gray-700'}`}>{value ?? 0}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
};

function formatUptime(seconds) {
  const s = Number(seconds) || 0;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default SystemLogs;
